import moment, { Moment } from 'moment-timezone';
import { useMemo, useState } from 'react';
import { AdminAvailabilityQuery, CareType, StateCodes } from '../../../../graphQL';
import { guessTz } from '../../../../utils';
import { useProviderNetworkContext } from '../../ProviderNetworkContext';
import { BookingArgs, Slot } from '../types';

type AvailabilityArgs = {
  variables: {
    providerId: number;
    careType: CareType;
    forDays: Moment[];
    forBooking?: BookingArgs;
    patientState?: StateCodes;
  };
  skip?: boolean;
};

export const useProviderAvailability = ({
  variables: { providerId, forDays, forBooking },
  skip,
}: AvailabilityArgs) => {
  const { duration } = forBooking ?? {};
  const {
    batchedIntakeAvailability,
    batchedIntakeAvailabilityLoading,
    batchedIntakeAvailabilityError,
    batchedSlotsByProvider,
    batchedBookingAvailability,
    batchedBookingAvailabilityLoading,
    batchedBookingAvailabilityError,
    batchedBookingSlotsByProvider,
  } = useProviderNetworkContext();

  // Guard against forDays being null/empty — callers pass `forDays: days!` and rely on
  // `skip` to suppress availability rendering. We never index into the array unguarded.
  const start = forDays?.[0];
  const end = forDays?.[forDays.length - 1]?.clone().endOf('day');
  const timezone = start?.tz() ?? guessTz();
  const isSkipped = skip || !start || !end;

  const [hoveredTime, setHoveredTime] = useState<Moment | null>(null);

  // Booking flow: read from batched data in context (O(1) Map lookup).
  const batchedBookingProviderData = useMemo(() => {
    if (!forBooking || !batchedBookingAvailability || !batchedBookingSlotsByProvider)
      return undefined;
    return {
      interval: batchedBookingAvailability.interval,
      data: batchedBookingSlotsByProvider.get(providerId) ?? [],
    };
  }, [forBooking, batchedBookingAvailability, batchedBookingSlotsByProvider, providerId]);

  // Org intake flow: read from batched data in context (O(1) Map lookup).
  const batchedIntakeProviderData = useMemo(() => {
    if (forBooking || !batchedIntakeAvailability || !batchedSlotsByProvider) return undefined;
    return {
      interval: batchedIntakeAvailability.interval,
      duration: batchedIntakeAvailability.duration,
      data: batchedSlotsByProvider.get(providerId) ?? [],
    };
  }, [forBooking, batchedIntakeAvailability, batchedSlotsByProvider, providerId]);

  const availabilityData = isSkipped
    ? undefined
    : forBooking
    ? batchedBookingProviderData
    : batchedIntakeProviderData;

  const hoverDuration = forBooking ? duration : batchedIntakeProviderData?.duration;
  const hoveredEnd = hoveredTime ? hoveredTime.clone().add(hoverDuration, 'minutes') : null;

  const timesByDay = useMemo(() => {
    if (!availabilityData || !start || !end) return [];
    return getTimeslotsByDay({
      availabilities: availabilityData.data,
      interval: availabilityData.interval,
      start,
      end,
      forDays,
      timeZone: timezone,
    });
  }, [start, end, timezone, forDays, availabilityData]);

  const loading = isSkipped
    ? false
    : forBooking
    ? batchedBookingAvailabilityLoading
    : batchedIntakeAvailabilityLoading;

  // Surface the underlying batched-query error so callers can distinguish a network
  // failure from a "no slots" result. Suppressed when the row is skipped.
  const error = isSkipped
    ? undefined
    : forBooking
    ? batchedBookingAvailabilityError
    : batchedIntakeAvailabilityError;

  return { timesByDay, loading, hoveredEnd, error, setHoveredTime, hoveredTime };
};

type GetTimeslotArgs = {
  availabilities: AdminAvailabilityQuery['adminAvailability']['data'];
  start: Moment;
  end: Moment;
  forDays: Moment[];
  timeZone: string;
  interval: number;
};

const getTimeslotsByDay = ({
  availabilities,
  start,
  end,
  forDays,
  timeZone,
  interval,
}: GetTimeslotArgs): Slot[][] => {
  const slotLookup = new Map<number, Slot>();
  const runner = moment(start).tz(timeZone);

  while (runner <= end) {
    const time = runner.clone();
    slotLookup.set(time.valueOf(), {
      allocationId: 0,
      time,
      available: false,
      blockers: [],
      organizationId: null,
    });
    runner.add(interval, 'minutes');
  }

  // Mark the timeslots which are available as such
  for (const availability of availabilities) {
    const time = moment(availability.start).clone().tz(timeZone);
    slotLookup.set(time.valueOf(), {
      allocationId: availability.allocationId,
      time,
      available: true,
      blockers: availability.blockers,
      organizationId: availability.organizationId,
      isFeeForServiceTime: availability.isFeeForServiceTime ?? false,
    });
  }

  const slots = [...slotLookup.values()];

  // Map timeslots to days
  const timesByDay = forDays.map(day => slots.filter(({ time }) => time.date() === day.date()));
  // Sort timeslots within each day
  timesByDay.forEach(day => day.sort((a, b) => a.time.diff(b.time)));

  return timesByDay;
};

export default useProviderAvailability;
